import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { PageContainer } from '@/components/common/PageContainer';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ArrowRight, UserX, RefreshCw, MoreVertical, Edit, Power, Package, Truck,
  Copy, Check, MapPin, History, Wallet,
} from 'lucide-react';
import { toast } from 'sonner';
import { DriverDetails as DriverDetailsType } from '@/types/driver.types';
import {
  DriverCapacityStats, DriverContactCard, DriverPaymentInfo, DriverVehicleInfo,
  DriverMetadata, DriverAssignedRoutes, DriverZonesList,
} from '@/components/features/drivers/details';
import { cn } from '@/lib/utils';
import { Route } from 'lucide-react';
import { EditDriverModal } from '@/components/features/drivers/EditDriverModal';
import { DriverFinancialDashboard } from '@/components/features/drivers/details/DriverFinancialDashboard';
import { useI18n } from '@/contexts/I18nContext';

export default function DriverDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { get, loading } = useApi();
  const { tp } = useI18n();
  
  const [driver, setDriver] = useState<DriverDetailsType | null>(null);
  const [copied, setCopied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    if (id) { setHasFetched(false); fetchDriverDetails(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchDriverDetails = async () => {
    setHasFetched(false);
    try {
      const result: any = await get(`/api/v1/drivers/${id}`);
      const driverData = result?.success !== undefined ? result?.data : result;
      if (driverData) setDriver(driverData); else setDriver(null);
    } catch (error) {
      toast.error(tp.drivers.errorLoadingDriverData);
      setDriver(null);
    } finally { setHasFetched(true); }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDriverDetails();
    setRefreshing(false);
    toast.success(tp.commonExt.refreshSuccess);
  };

  const handleCopyId = async () => {
    if (id) {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      toast.success(tp.drivers.driverIdCopied);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleBack = () => navigate('/dashboard/drivers');

  if ((!hasFetched || loading) && !driver) return (
    <PageContainer><div className="space-y-6"><LoadingSkeleton type="card" rows={1} /><div className="grid grid-cols-1 lg:grid-cols-2 gap-4"><LoadingSkeleton type="card" rows={2} /><LoadingSkeleton type="card" rows={2} /></div></div></PageContainer>
  );

  if (hasFetched && !driver) return (
    <PageContainer><EmptyState icon={<UserX className="h-16 w-16" />} title={tp.drivers.driverNotFound} description={tp.drivers.driverNotFoundDescription} action={{ label: tp.drivers.backToListButton, onClick: handleBack }} /></PageContainer>
  );

  const vehicleEmoji = getVehicleEmoji(driver.vehicle_type || '');
  const capacityName = driver.capacity_name?.ar || driver.capacity_name?.en || driver.carrier_name || '-';

  return (
    <PageContainer>
      <div className="mb-6 space-y-4" dir="rtl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={handleBack} className="h-9 w-9"><ArrowRight className="h-5 w-5" /></Button>
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-2xl">{vehicleEmoji}</div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl md:text-2xl font-bold">{driver.name}</h1>
                  <Badge variant="outline" className={cn("text-xs", driver.is_active ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200")}>
                    {driver.is_active ? tp.drivers.statusActive : tp.drivers.statusInactive}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">{capacityName}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 me-12 sm:me-0">
            <Button variant="outline" size="sm" onClick={handleCopyId} className="h-9">
              {copied ? <Check className="h-4 w-4 ms-2 text-green-500" /> : <Copy className="h-4 w-4 ms-2" />}
              {tp.drivers.copyIdButton}
            </Button>
            <Button variant="outline" size="icon" onClick={handleRefresh} disabled={refreshing} className="h-9 w-9">
              <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="outline" size="icon" className="h-9 w-9"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => setShowEditModal(true)}><Edit className="h-4 w-4 ms-2" />{tp.drivers.editDataMenuItem}</DropdownMenuItem>
                <DropdownMenuItem><Power className="h-4 w-4 ms-2" />{driver.is_active ? tp.drivers.disableDriverMenuItem : tp.drivers.enableDriverMenuItem}</DropdownMenuItem>
                <DropdownMenuItem><Package className="h-4 w-4 ms-2" />{tp.drivers.assignShipmentMenuItem}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <QuickStatCard label={tp.drivers.currentShipmentsLabel} value={driver.current_shipments ?? 0} max={driver.capacity?.max_shipments} icon={<Package className="h-4 w-4" />} warning={(driver.available_slots ?? 0) === 0 && !!driver.capacity} />
          <QuickStatCard label={tp.drivers.availableLabel} value={driver.available_slots ?? '-'} suffix={driver.available_slots !== undefined ? tp.drivers.shipmentUnitLabel : undefined} icon={<Truck className="h-4 w-4" />} success={(driver.available_slots ?? 0) > 0} />
          <QuickStatCard label={tp.drivers.usedCapacityLabel} value={`${parseFloat(String(driver.used_capacity_m3 || 0)).toFixed(1)}`} suffix={tp.drivers.cubicMeterUnit} icon={<Package className="h-4 w-4" />} />
          <QuickStatCard label={tp.drivers.usedWeightLabel} value={parseFloat(String(driver.used_weight_kg || 0)).toFixed(0)} suffix={tp.drivers.kilogramUnit} icon={<Package className="h-4 w-4" />} />
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-4" dir="rtl">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="overview">{tp.drivers.tabOverview}</TabsTrigger>
          <TabsTrigger value="zones">{tp.drivers.tabZones}</TabsTrigger>
          <TabsTrigger value="routes">{tp.drivers.tabRoutes}</TabsTrigger>
          <TabsTrigger value="shipments">{tp.drivers.tabShipments}</TabsTrigger>
          <TabsTrigger value="location">{tp.drivers.tabLocation}</TabsTrigger>
          <TabsTrigger value="history">{tp.drivers.tabHistory}</TabsTrigger>
          <TabsTrigger value="financial"><Wallet className="h-4 w-4 ml-1" />{tp.drivers.tabFinancial}</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-4"><DriverContactCard driver={driver} /><DriverPaymentInfo driver={driver} /></div>
            <div className="space-y-4"><DriverCapacityStats driver={driver} /><DriverVehicleInfo driver={driver} /><DriverMetadata driver={driver} /></div>
          </div>
        </TabsContent>
        <TabsContent value="zones"><DriverZonesList driverProfileId={id!} /></TabsContent>
        <TabsContent value="routes"><DriverAssignedRoutes driverProfileId={id!} /></TabsContent>
        <TabsContent value="shipments">
          <Card><CardContent className="py-12"><EmptyState icon={<Package className="h-12 w-12" />} title={tp.drivers.shipmentsListTitle} description={tp.drivers.shipmentsListDescription} action={{ label: tp.drivers.viewShipmentsButton, onClick: () => navigate(`/dashboard/shipments?driver=${id}`) }} /></CardContent></Card>
        </TabsContent>
        <TabsContent value="location">
          <Card><CardContent className="py-12"><EmptyState icon={<MapPin className="h-12 w-12" />} title={tp.drivers.trackLocationTitle} description={tp.drivers.trackLocationDescription} /></CardContent></Card>
        </TabsContent>
        <TabsContent value="history">
          <Card><CardContent className="py-12"><EmptyState icon={<History className="h-12 w-12" />} title={tp.drivers.activityHistoryTitle} description={tp.drivers.activityHistoryDescription} /></CardContent></Card>
        </TabsContent>
        <TabsContent value="financial"><DriverFinancialDashboard driverId={id!} driver={driver} /></TabsContent>
      </Tabs>

      <EditDriverModal open={showEditModal} onOpenChange={setShowEditModal} driver={driver} onSuccess={fetchDriverDetails} />
    </PageContainer>
  );
}

interface QuickStatCardProps { label: string; value: number | string; max?: number; suffix?: string; icon: React.ReactNode; warning?: boolean; success?: boolean; }
function QuickStatCard({ label, value, max, suffix, icon, warning, success }: QuickStatCardProps) {
  return (
    <Card className={cn("transition-colors", warning && "border-destructive/50 bg-destructive/5", success && "border-green-500/50 bg-green-500/5")}>
      <CardContent className="p-3">
        <div className="flex items-center gap-2 mb-1">
          <span className={cn("text-muted-foreground", warning && "text-destructive", success && "text-green-600")}>{icon}</span>
          <span className="text-xs text-muted-foreground">{label}</span>
        </div>
        <div className="flex items-baseline gap-1">
          <span className={cn("text-lg font-bold", warning && "text-destructive", success && "text-green-600")}>{value}</span>
          {max !== undefined && <span className="text-sm text-muted-foreground">/ {max}</span>}
          {suffix && <span className="text-xs text-muted-foreground">{suffix}</span>}
        </div>
      </CardContent>
    </Card>
  );
}

function getVehicleEmoji(type: string): string {
  const emojis: Record<string, string> = { motorcycle: '🏙️', van: '🚐', truck: '🚛', car: '🚗', bicycle: '🚲' };
  return emojis[type] || '🚗';
}
